import React, { useMemo, useState } from "react";
import { EDataTable } from "../../../components/EDataTable";
import { TableActions } from "../../../components/TableActions";
import { TextButton } from "../../../components/ButtonComponents";
import { Edit, Trash2, ArrowDownCircle, ArrowUpCircle, Wallet, Landmark, HelpCircle, PlusCircle, MinusCircle, Receipt, ScrollText, Calendar } from "lucide-react";
import { toast } from "react-toastify";

import { AccountMovement } from "../../../types/accountMovement";
import { useAccountMovementStore } from "../../../store/accountMovementStore";
import { useConfirmModal } from "../../../hooks/useConfirmModal";
import { useApiErrorHandler } from "../../../hooks/useApiErrorHandler";
import { DataTableColumn, GroupFilter } from "../../../types";
import { DateRangePicker } from "../../../components/DateRangePicker";

import { CollectionPaymentDialog } from "./CollectionPaymentDialog";

type TabFilter = "ALL" | "CASH_BANK" | "CHECK" | "PROMISSORY";

export const CollectionPaymentList = () => {
    const store = useAccountMovementStore();
    const { deleteItem, createItem, updateItem } = store;
    const { handleApiError } = useApiErrorHandler();

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [movementToEdit, setMovementToEdit] = useState<AccountMovement | null>(null);
    const [activeTab, setActiveTab] = useState<TabFilter>("ALL");
    const [filterDates, setFilterDates] = useState<(Date | null)[] | null>(null);

    const handleNew = (type: 'COLLECTION_IN' | 'PAYMENT_OUT' | 'CHECK_IN' | 'CHECK_OUT' | 'PROMISSORY_IN' | 'PROMISSORY_OUT') => {
        setMovementToEdit({
            type,
            amount: 0,
            paymentLocation: ['CHECK_IN', 'CHECK_OUT', 'PROMISSORY_IN', 'PROMISSORY_OUT'].includes(type) ? 'OTHER' : 'CASH',
            date: new Date(),
            dueDate: ['CHECK_IN', 'CHECK_OUT', 'PROMISSORY_IN', 'PROMISSORY_OUT'].includes(type) ? new Date() : undefined,
        } as any);
        setIsModalOpen(true);
    };

    const movementFilter: GroupFilter = useMemo(() => {
        if (activeTab === "CASH_BANK") {
            return {
                logic: "or",
                filters: [
                    { field: "type", operator: "eq", value: "COLLECTION_IN" },
                    { field: "type", operator: "eq", value: "PAYMENT_OUT" }
                ]
            };
        }
        if (activeTab === "CHECK") {
            return {
                logic: "or",
                filters: [
                    { field: "type", operator: "eq", value: "CHECK_IN" },
                    { field: "type", operator: "eq", value: "CHECK_OUT" }
                ]
            };
        }
        if (activeTab === "PROMISSORY") {
            return {
                logic: "or",
                filters: [
                    { field: "type", operator: "eq", value: "PROMISSORY_IN" },
                    { field: "type", operator: "eq", value: "PROMISSORY_OUT" }
                ]
            };
        }
        // ALL
        return {
            logic: "or",
            filters: [
                { field: "type", operator: "eq", value: "COLLECTION_IN" },
                { field: "type", operator: "eq", value: "PAYMENT_OUT" },
                { field: "type", operator: "eq", value: "CHECK_IN" },
                { field: "type", operator: "eq", value: "CHECK_OUT" },
                { field: "type", operator: "eq", value: "PROMISSORY_IN" },
                { field: "type", operator: "eq", value: "PROMISSORY_OUT" }
            ]
        };
    }, [activeTab]);

    const combinedFilters = useMemo(() => {
        const filters: GroupFilter[] = [movementFilter];
        if (filterDates && Array.isArray(filterDates) && filterDates[0] && filterDates[1]) {
            const startDate = new Date(filterDates[0]);
            startDate.setHours(0, 0, 0, 0);
            const endDate = new Date(filterDates[1]);
            endDate.setHours(23, 59, 59, 999);
            filters.push({
                logic: "and",
                filters: [
                    { field: "date", operator: "gte", value: startDate.toISOString() },
                    { field: "date", operator: "lte", value: endDate.toISOString() }
                ]
            });
        }
        return filters;
    }, [movementFilter, filterDates]);

    const handleEdit = (movement: AccountMovement) => {
        setMovementToEdit(movement);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setMovementToEdit(null);
    };

    const handleSave = async (payload: any) => {
        try {
            if (movementToEdit?.id) {
                await updateItem(movementToEdit.id, payload);
                toast.success("Kayıt başarıyla güncellendi.");
            } else {
                await createItem(payload);
                toast.success("Yeni kayıt başarıyla eklendi.");
            }
            handleCloseModal();
        } catch (error) {
            handleApiError(error);
        }
    };

    const { confirm, modal: confirmModal } = useConfirmModal();

    const handleDeleteClick = async (movement: AccountMovement) => {
        const ok = await confirm(`${movement.reference || movement.type} belgesini silmek istiyor musunuz?`);
        if (ok) {
            try {
                await deleteItem(movement.id);
                toast.success("Kayıt başarıyla silindi.");
            } catch (error) {
                handleApiError(error);
            }
        }
    };

    // --- RENDER TEMPLATES ---

    const typeBodyTemplate = (movement: AccountMovement) => {
        let label = movement.type as string;
        let icon = ArrowDownCircle;
        let colorClass = 'text-slate-700 dark:text-slate-300';
        let badgeBg = 'bg-slate-100 dark:bg-slate-800 border-slate-300';

        switch (movement.type) {
            case 'COLLECTION_IN':
                label = 'Tahsilat (Nakit/Banka)';
                icon = ArrowDownCircle;
                colorClass = 'text-emerald-700 dark:text-emerald-400';
                badgeBg = 'bg-emerald-50 dark:bg-emerald-950/40 border-emerald-200 dark:border-emerald-800';
                break;
            case 'PAYMENT_OUT':
                label = 'Ödeme (Nakit/Banka)';
                icon = ArrowUpCircle;
                colorClass = 'text-red-700 dark:text-red-400';
                badgeBg = 'bg-red-50 dark:bg-red-950/40 border-red-200 dark:border-red-800';
                break;
            case 'CHECK_IN':
                label = 'Müşteri Çeki Girişi';
                icon = Receipt;
                colorClass = 'text-purple-700 dark:text-purple-400';
                badgeBg = 'bg-purple-50 dark:bg-purple-950/40 border-purple-200 dark:border-purple-800';
                break;
            case 'CHECK_OUT':
                label = 'Çek Çıkışı / Ciro';
                icon = Receipt;
                colorClass = 'text-purple-900 dark:text-purple-300';
                badgeBg = 'bg-purple-100 dark:bg-purple-950/60 border-purple-300 dark:border-purple-700';
                break;
            case 'PROMISSORY_IN':
                label = 'Müşteri Senet Girişi';
                icon = ScrollText;
                colorClass = 'text-amber-700 dark:text-amber-400';
                badgeBg = 'bg-amber-50 dark:bg-amber-950/40 border-amber-200 dark:border-amber-800';
                break;
            case 'PROMISSORY_OUT':
                label = 'Senet Çıkışı / Ciro';
                icon = ScrollText;
                colorClass = 'text-amber-900 dark:text-amber-300';
                badgeBg = 'bg-amber-100 dark:bg-amber-950/60 border-amber-300 dark:border-amber-700';
                break;
        }

        return (
            <div className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-semibold border ${colorClass} ${badgeBg}`}>
                {React.createElement(icon, { className: "w-3.5 h-3.5 mr-1.5 shrink-0" })}
                <span>{label}</span>
            </div>
        );
    };

    const locationBodyTemplate = (movement: any) => {
        const isCheck = ['CHECK_IN', 'CHECK_OUT'].includes(movement.type);
        const isPromissory = ['PROMISSORY_IN', 'PROMISSORY_OUT'].includes(movement.type);

        if (isCheck) {
            return (
                <div className="flex items-center gap-1.5 text-xs font-medium text-purple-700 dark:text-purple-400">
                    <Receipt size={14} />
                    <span>Çek Portföyü</span>
                </div>
            );
        }

        if (isPromissory) {
            return (
                <div className="flex items-center gap-1.5 text-xs font-medium text-amber-700 dark:text-amber-400">
                    <ScrollText size={14} />
                    <span>Senet Portföyü</span>
                </div>
            );
        }

        const isCash = movement.paymentLocation === 'CASH';
        const isBank = movement.paymentLocation === 'BANK';

        let icon = HelpCircle;
        let label = "Diğer";
        let subLabel = "";

        if (isCash) {
            icon = Wallet;
            label = "Kasa";
            subLabel = movement.cash?.name;
        } else if (isBank) {
            icon = Landmark;
            label = "Banka";
            subLabel = movement.bank?.name;
        }

        return (
            <div className="flex flex-col leading-tight">
                <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider opacity-70">
                    {React.createElement(icon, { size: 10 })}
                    <span>{label}</span>
                </div>
                <span className="font-semibold">{subLabel || 'Belirtilmemiş'}</span>
            </div>
        );
    };

    const amountBodyTemplate = (movement: AccountMovement) => {
        const isInDirection = ['COLLECTION_IN', 'CHECK_IN', 'PROMISSORY_IN'].includes(movement.type);
        return (
            <span className={`font-bold text-base ${isInDirection ? 'text-green-700 dark:text-green-400' : 'text-red-700 dark:text-red-400'}`}>
                {isInDirection ? '+' : '-'} {new Intl.NumberFormat('tr-TR', {
                    style: 'currency',
                    currency: movement.currency === 'TL' ? 'TRY' : (movement.currency || 'TRY')
                }).format(movement.amount)}
            </span>
        );
    };

    const dateBodyTemplate = (movement: AccountMovement) => (
        <div className="flex flex-col text-sm">
            <span className="font-medium">{new Date(movement.date).toLocaleDateString('tr-TR')}</span>
            {movement.dueDate && (
                <span className="text-[11px] font-semibold text-purple-700 dark:text-purple-400 flex items-center gap-0.5 mt-0.5">
                    <Calendar size={10} />
                    Vade: {new Date(movement.dueDate).toLocaleDateString('tr-TR')}
                </span>
            )}
        </div>
    );

    const actionBodyTemplate = (movement: AccountMovement) => (
        <TableActions
            actions={[
                {
                    icon: Edit,
                    colorClass: "text-blue-600",
                    title: "Düzenle",
                    onClick: () => handleEdit(movement)
                },
                {
                    icon: Trash2,
                    colorClass: "text-red-500",
                    title: "Sil",
                    onClick: () => handleDeleteClick(movement)
                }
            ]}
        />
    );

    const columns: DataTableColumn<AccountMovement>[] = [
        { field: "date", header: "Tarih / Vade", width: "150px", body: dateBodyTemplate, sortable: true },
        { field: "type", header: "İşlem Türü", width: "190px", body: typeBodyTemplate, sortable: true },
        { field: "account.name", header: "Cari Hesap", width: "250px", sortable: true, body: (m) => <span className="font-bold">{m.account?.name}</span> },
        { field: "paymentLocation", header: "Ödeme Kanalı / Portföy", width: "180px", body: locationBodyTemplate },
        { field: "reference", header: "Belge / Çek No", width: "160px", sortable: true, body: (m) => <span className="font-mono">{m.reference || '---'}</span> },
        { field: "amount", header: "Tutar", width: "170px", align: "right", body: amountBodyTemplate, sortable: true },
        {
            field: "actions",
            header: "İşlemler",
            width: "60px", align: "center", body: actionBodyTemplate
        },
    ];

    const tabs: { key: TabFilter; label: string }[] = [
        { key: "ALL", label: "Tümü" },
        { key: "CASH_BANK", label: "Nakit & Banka" },
        { key: "CHECK", label: "Çekler" },
        { key: "PROMISSORY", label: "Senetler" },
    ];

    return (
        <div className="h-full">
            {confirmModal}

            <EDataTable<AccountMovement>
                tableKey="collection_payment_list"
                useStore={() => store}
                dataKey="id"
                title="Finansal Hareketler (Kasa, Banka, Çek & Senet)"
                placeholder="Cari adı veya belge / çek no ara..."
                filters={combinedFilters}
                filterBar={
                    <div className="flex flex-wrap items-center gap-3 w-full xl:w-auto mb-2">
                        {/* Tab Filtreleri */}
                        <div className="flex bg-bgAlt p-1 rounded-lg border border-borderColor">
                            {tabs.map((tab) => (
                                <button
                                    key={tab.key}
                                    onClick={() => setActiveTab(tab.key)}
                                    className={`px-3 py-1 text-xs font-bold rounded-md transition-all ${activeTab === tab.key
                                        ? "bg-primary text-white shadow-sm"
                                        : "text-text opacity-70 hover:opacity-100"
                                    }`}
                                >
                                    {tab.label}
                                </button>
                            ))}
                        </div>

                        <DateRangePicker value={filterDates as any} onChange={setFilterDates as any} />
                    </div>
                }
                searchFields={[
                    { field: "reference", operator: "contains" },
                    { field: "account.name", operator: "contains" },
                ]}
                actions={
                    <div className="flex flex-wrap gap-2">
                        <TextButton onClick={() => handleNew('COLLECTION_IN')} className="bg-emerald-600 text-white hover:bg-emerald-700 px-3 py-2 rounded-lg flex items-center text-xs font-bold">
                            <PlusCircle className="w-3.5 h-3.5 mr-1.5" /> + Tahsilat
                        </TextButton>
                        <TextButton onClick={() => handleNew('PAYMENT_OUT')} className="bg-red-600 text-white hover:bg-red-700 px-3 py-2 rounded-lg flex items-center text-xs font-bold">
                            <MinusCircle className="w-3.5 h-3.5 mr-1.5" /> + Ödeme
                        </TextButton>
                        <TextButton onClick={() => handleNew('CHECK_IN')} className="bg-purple-600 text-white hover:bg-purple-700 px-3 py-2 rounded-lg flex items-center text-xs font-bold">
                            <Receipt className="w-3.5 h-3.5 mr-1.5" /> + Çek Girişi
                        </TextButton>
                        <TextButton onClick={() => handleNew('CHECK_OUT')} className="bg-purple-800 text-white hover:bg-purple-900 px-3 py-2 rounded-lg flex items-center text-xs font-bold">
                            <Receipt className="w-3.5 h-3.5 mr-1.5" /> + Çek Çıkışı
                        </TextButton>
                        <TextButton onClick={() => handleNew('PROMISSORY_IN')} className="bg-amber-600 text-white hover:bg-amber-700 px-3 py-2 rounded-lg flex items-center text-xs font-bold">
                            <ScrollText className="w-3.5 h-3.5 mr-1.5" /> + Senet Girişi
                        </TextButton>
                    </div>
                }
                columns={columns}
            />

            <CollectionPaymentDialog
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                movement={movementToEdit}
                onSave={handleSave}
            />
        </div>
    );
};
